import React, { useState, useEffect } from "react";
import {
  FaVolumeUp,
  FaVolumeMute,
  FaStar,
  FaBookOpen,
  FaCheck,
  FaMusic
} from "react-icons/fa";

/**
 * Phonics Data A-Z with phonetic symbols, short/primary sounds, 
 * example words, emojis, and educational phonics helper hints.
 */
const PHONICS_DATA = {
  A: { letter: "A", soundSymbol: "/æ/", soundName: "short 'a' sound", phonemeSound: "ah", exampleWord: "Apple", exampleEmoji: "🍎", hint: "A says /æ/, /æ/, apple!" },
  B: { letter: "B", soundSymbol: "/b/", soundName: "buh sound", phonemeSound: "buh", exampleWord: "Ball", exampleEmoji: "⚽", hint: "B says /b/, /b/, ball!" },
  C: { letter: "C", soundSymbol: "/k/", soundName: "kuh sound", phonemeSound: "kuh", exampleWord: "Cat", exampleEmoji: "🐱", hint: "C says /k/, /k/, cat!" },
  D: { letter: "D", soundSymbol: "/d/", soundName: "duh sound", phonemeSound: "duh", exampleWord: "Dog", exampleEmoji: "🐶", hint: "D says /d/, /d/, dog!" },
  E: { letter: "E", soundSymbol: "/ɛ/", soundName: "short 'e' sound", phonemeSound: "eh", exampleWord: "Elephant", exampleEmoji: "🐘", hint: "E says /ɛ/, /ɛ/, elephant!" },
  F: { letter: "F", soundSymbol: "/f/", soundName: "fff sound", phonemeSound: "ffff", exampleWord: "Fish", exampleEmoji: "🐟", hint: "F says /f/, /f/, fish!" },
  G: { letter: "G", soundSymbol: "/g/", soundName: "guh sound", phonemeSound: "guh", exampleWord: "Goat", exampleEmoji: "🐐", hint: "G says /g/, /g/, goat!" },
  H: { letter: "H", soundSymbol: "/h/", soundName: "huh sound", phonemeSound: "huh", exampleWord: "Hat", exampleEmoji: "🎩", hint: "H says /h/, /h/, hat!" },
  I: { letter: "I", soundSymbol: "/ɪ/", soundName: "short 'i' sound", phonemeSound: "ih", exampleWord: "Igloo", exampleEmoji: "❄️", hint: "I says /ɪ/, /ɪ/, igloo!" },
  J: { letter: "J", soundSymbol: "/ʤ/", soundName: "juh sound", phonemeSound: "juh", exampleWord: "Juice", exampleEmoji: "🧃", hint: "J says /ʤ/, /ʤ/, juice!" },
  K: { letter: "K", soundSymbol: "/k/", soundName: "kuh sound", phonemeSound: "kuh", exampleWord: "Kite", exampleEmoji: "🪁", hint: "K says /k/, /k/, kite!" },
  L: { letter: "L", soundSymbol: "/l/", soundName: "lll sound", phonemeSound: "lllll", exampleWord: "Lion", exampleEmoji: "🦁", hint: "L says /l/, /l/, lion!" },
  M: { letter: "M", soundSymbol: "/m/", soundName: "mmm sound", phonemeSound: "mmmmm", exampleWord: "Monkey", exampleEmoji: "🐵", hint: "M says /m/, /m/, monkey!" },
  N: { letter: "N", soundSymbol: "/n/", soundName: "nnn sound", phonemeSound: "nnnnn", exampleWord: "Nest", exampleEmoji: "🪺", hint: "N says /n/, /n/, nest!" },
  O: { letter: "O", soundSymbol: "/ɒ/", soundName: "short 'o' sound", phonemeSound: "aw", exampleWord: "Octopus", exampleEmoji: "🐙", hint: "O says /ɒ/, /ɒ/, octopus!" },
  P: { letter: "P", soundSymbol: "/p/", soundName: "puh sound", phonemeSound: "puh", exampleWord: "Penguin", exampleEmoji: "🐧", hint: "P says /p/, /p/, penguin!" },
  Q: { letter: "Q", soundSymbol: "/kw/", soundName: "kwuh sound", phonemeSound: "kwuh", exampleWord: "Queen", exampleEmoji: "👑", hint: "Q says /kw/, /kw/, queen!" },
  R: { letter: "R", soundSymbol: "/r/", soundName: "rrr sound", phonemeSound: "rrrr", exampleWord: "Rabbit", exampleEmoji: "🐰", hint: "R says /r/, /r/, rabbit!" },
  S: { letter: "S", soundSymbol: "/s/", soundName: "sss sound", phonemeSound: "sssss", exampleWord: "Sun", exampleEmoji: "☀️", hint: "S says /s/, /s/, sun!" },
  T: { letter: "T", soundSymbol: "/t/", soundName: "tuh sound", phonemeSound: "tuh", exampleWord: "Tiger", exampleEmoji: "🐅", hint: "T says /t/, /t/, tiger!" },
  U: { letter: "U", soundSymbol: "/ʌ/", soundName: "short 'u' sound", phonemeSound: "uh", exampleWord: "Umbrella", exampleEmoji: "☂️", hint: "U says /ʌ/, /ʌ/, umbrella!" },
  V: { letter: "V", soundSymbol: "/v/", soundName: "vvv sound", phonemeSound: "vvvv", exampleWord: "Violin", exampleEmoji: "🎻", hint: "V says /v/, /v/, violin!" },
  W: { letter: "W", soundSymbol: "/w/", soundName: "wuh sound", phonemeSound: "wuh", exampleWord: "Whale", exampleEmoji: "🐳", hint: "W says /w/, /w/, whale!" },
  X: { letter: "X", soundSymbol: "/ks/", soundName: "ks sound", phonemeSound: "ks", exampleWord: "Xylophone", exampleEmoji: "🎹", hint: "X says /ks/, /ks/, xylophone!" },
  Y: { letter: "Y", soundSymbol: "/j/", soundName: "yuh sound", phonemeSound: "yuh", exampleWord: "Yacht", exampleEmoji: "⛵", hint: "Y says /j/, /j/, yacht!" },
  Z: { letter: "Z", soundSymbol: "/z/", soundName: "zzz sound", phonemeSound: "zzzz", exampleWord: "Zebra", exampleEmoji: "🦓", hint: "Z says /z/, /z/, zebra!" }
};

export default function PhonicsAlphabetLearning() {
  const [selectedLetter, setSelectedLetter] = useState("A");
  const [isMuted, setIsMuted] = useState(false);
  const [isPlayingSound, setIsPlayingSound] = useState(false);

  const alphabetList = Object.keys(PHONICS_DATA);
  const currentData = PHONICS_DATA[selectedLetter];

  useEffect(() => {
    playPhonicsAudio(true);
  }, [selectedLetter]);

  const playPhonicsAudio = (isAuto = false) => {
    if (isMuted && !isAuto) return;
    if (!("speechSynthesis" in window)) return;

    window.speechSynthesis.cancel();
    setIsPlayingSound(true);

    const speechText = `${currentData.phonemeSound}... ${currentData.letter}, is for, ${currentData.exampleWord}!`;
    
    const utterance = new SpeechSynthesisUtterance(speechText);
    utterance.pitch = 1.3;
    utterance.rate = 0.75;

    utterance.onend = () => setIsPlayingSound(false);
    utterance.onerror = () => setIsPlayingSound(false);

    if (!isMuted) {
      window.speechSynthesis.speak(utterance);
    } else {
      setIsPlayingSound(false);
    }
  };

  const toggleMute = () => {
    const nextMute = !isMuted;
    setIsMuted(nextMute);
    if (nextMute && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      setIsPlayingSound(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Dynamic responsive CSS overrides for smooth breakpoints */}
      <style>{`
        .letter-grid-scroll::-webkit-scrollbar {
          display: none;
        }
        .letter-grid-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        @media (max-width: 640px) {
          .phonics-workspace {
            flex-direction: column !important;
          }
          .phonics-left-panel {
            border-right: none !important;
            border-bottom: 2px solid #F1F5F9 !important;
            padding: 16px !important;
          }
          .phonics-right-panel {
            padding: 16px !important;
          }
          .phonics-header {
            flex-direction: row !important;
            justify-content: space-between !important;
            padding: 12px 16px !important;
          }
          .phonics-header-title {
            font-size: 1.1rem !important;
          }
          .phonics-header-subtitle {
            font-size: 0.75rem !important;
          }
          .letter-chip-btn {
            min-width: 36px !important;
            height: 38px !important;
            font-size: 0.9rem !important;
          }
          .upper-char-text {
            font-size: 3.8rem !important;
          }
          .lower-char-text {
            font-size: 2.8rem !important;
          }
          .emoji-bubble-text {
            font-size: 3.8rem !important;
          }
        }

        @media (min-width: 641px) and (max-width: 1024px) {
          .phonics-workspace {
            flex-direction: row !important;
          }
          .letter-chip-btn {
            min-width: 32px !important;
            height: 36px !important;
          }
        }
      `}</style>

      <div style={styles.card}>
        {/* Header Section */}
        <div style={styles.header} className="phonics-header">
          <div style={styles.headerLeft}>
            <div style={styles.iconCircle}>
              <FaBookOpen size={18} color="#7C3AED" />
            </div>
            <div>
              <h1 style={styles.title} className="phonics-header-title">Phonics Alphabet Explorer</h1>
              <p style={styles.subtitle} className="phonics-header-subtitle">Master letter sounds and vocabulary step-by-step</p>
            </div>
          </div>
          <button
            onClick={toggleMute}
            style={styles.globalMuteBtn}
            title={isMuted ? "Unmute Voice" : "Mute Voice"}
          >
            {isMuted ? <FaVolumeMute size={16} color="#EF4444" /> : <FaVolumeUp size={16} color="#7C3AED" />}
            <span>{isMuted ? "Sound Off" : "Sound On"}</span>
          </button>
        </div>

        {/* Letter Selector Grid / Bar */}
        <div style={styles.selectorBar}>
          <span style={styles.selectorLabel}>Select a Letter:</span>
          <div style={styles.letterGrid} className="letter-grid-scroll">
            {alphabetList.map((char) => {
              const isSelected = selectedLetter === char;
              return (
                <button
                  key={char}
                  onClick={() => setSelectedLetter(char)}
                  className="letter-chip-btn"
                  style={{
                    ...styles.letterChip,
                    backgroundColor: isSelected ? "#7C3AED" : "#F5F3FF",
                    color: isSelected ? "#FFFFFF" : "#4C1D95",
                    borderColor: isSelected ? "#6D28D9" : "#DDD6FE",
                    transform: isSelected ? "scale(1.05)" : "scale(1)"
                  }}
                >
                  {char}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Interactive Workspace */}
        <div style={styles.workspace} className="phonics-workspace">
          {/* Left Panel: Letter Sound Focus Card */}
          <div style={styles.leftPanel} className="phonics-left-panel">
            <div style={styles.soundCardTop}>
              <span style={styles.phoneticSymbolBadge}>{currentData.soundSymbol}</span>
              <span style={styles.soundDescription}>{currentData.soundName}</span>
            </div>

            <div style={styles.bigLetterDisplay}>
              <span style={styles.upperChar} className="upper-char-text">{currentData.letter}</span>
              <span style={styles.lowerChar} className="lower-char-text">{currentData.letter.toLowerCase()}</span>
            </div>

            <button
              onClick={() => playPhonicsAudio(false)}
              style={{
                ...styles.playSoundActionBtn,
                backgroundColor: isPlayingSound ? "#4C1D95" : "#7C3AED"
              }}
            >
              <FaMusic size={15} />
              <span>{isPlayingSound ? "Playing Sound..." : `Hear Sound (${currentData.phonemeSound})`}</span>
            </button>

            <p style={styles.phonicsHintText}>{currentData.hint}</p>
          </div>

          {/* Right Panel: Example Showcase Panel */}
          <div style={styles.rightPanel} className="phonics-right-panel">
            <div style={styles.rightPanelHeader}>
              <FaStar size={15} color="#F59E0B" style={{ marginRight: 6 }} />
              <span>Phonics Example Word</span>
            </div>

            <div style={styles.exampleContentCard}>
              <div style={styles.emojiBubble} className="emoji-bubble-text">{currentData.exampleEmoji}</div>
              
              <div style={styles.wordDescriptionBlock}>
                <h2 style={styles.exampleWordTitle}>{currentData.exampleWord}</h2>
                <p style={styles.wordPhonicsSub}>
                  Starts with <strong style={{ color: "#7C3AED" }}>{currentData.letter}</strong> making the 
                  <span style={styles.inlinePhoneme}>{currentData.soundSymbol}</span> sound
                </p>
              </div>

              <div style={styles.successCheckRow}>
                <div style={styles.checkIconBox}>
                  <FaCheck size={13} color="#16A34A" />
                </div>
                <span style={styles.checkText}>Verified Cambridge Phonics Standard</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#F3E8FF",
    padding: "12px",
    boxSizing: "border-box",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif"
  },
  card: {
    width: "100%",
    maxWidth: "880px",
    backgroundColor: "#FFFFFF",
    borderRadius: "20px",
    boxShadow: "0 10px 25px -5px rgba(124, 58, 237, 0.15)",
    border: "3px solid #DDD6FE",
    overflow: "hidden"
  },
  header: {
    background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#FFFFFF",
    flexWrap: "wrap",
    gap: "10px"
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  iconCircle: {
    backgroundColor: "#FFFFFF",
    borderRadius: "50%",
    padding: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  title: {
    margin: 0,
    fontSize: "1.25rem",
    fontWeight: 800
  },
  subtitle: {
    margin: 0,
    fontSize: "0.8rem",
    opacity: 0.9
  },
  globalMuteBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#FFFFFF",
    color: "#5B21B6",
    border: "none",
    padding: "8px 12px",
    borderRadius: "10px",
    fontWeight: "bold",
    fontSize: "0.8rem",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    whiteSpace: "nowrap"
  },
  selectorBar: {
    backgroundColor: "#FAF5FF",
    padding: "12px 16px",
    borderBottom: "2px solid #EDE9FE",
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  selectorLabel: {
    fontWeight: "bold",
    color: "#5B21B6",
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  letterGrid: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    overflowX: "auto",
    paddingBottom: "4px"
  },
  letterChip: {
    minWidth: "30px",
    height: "36px",
    borderRadius: "8px",
    fontSize: "0.9rem",
    fontWeight: "800",
    cursor: "pointer",
    border: "2px solid",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  workspace: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    minHeight: "360px"
  },
  leftPanel: {
    flex: "1 1 50%",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRight: "2px solid #F1F5F9",
    textAlign: "center"
  },
  soundCardTop: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "10px",
    flexWrap: "wrap",
    justifyContent: "center"
  },
  phoneticSymbolBadge: {
    backgroundColor: "#EDE9FE",
    color: "#6D28D9",
    padding: "4px 8px",
    borderRadius: "8px",
    fontWeight: "bold",
    fontSize: "0.85rem",
    border: "1px solid #DDD6FE"
  },
  soundDescription: {
    fontSize: "0.8rem",
    color: "#64748B",
    fontWeight: "600",
    textTransform: "uppercase"
  },
  bigLetterDisplay: {
    display: "flex",
    alignItems: "baseline",
    gap: "10px",
    margin: "8px 0"
  },
  upperChar: {
    fontSize: "4.5rem",
    fontWeight: "900",
    color: "#4C1D95",
    lineHeight: 1
  },
  lowerChar: {
    fontSize: "3.2rem",
    fontWeight: "700",
    color: "#7C3AED",
    lineHeight: 1
  },
  playSoundActionBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#FFFFFF",
    border: "none",
    padding: "10px 18px",
    borderRadius: "12px",
    fontWeight: "bold",
    fontSize: "0.9rem",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(124, 58, 237, 0.25)",
    margin: "10px 0",
    transition: "transform 0.1s ease"
  },
  phonicsHintText: {
    margin: "6px 0 0 0",
    color: "#6D28D9",
    fontStyle: "italic",
    fontWeight: "600",
    fontSize: "0.9rem"
  },
  rightPanel: {
    flex: "1 1 50%",
    padding: "24px",
    backgroundColor: "#FAFAFA",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
  },
  rightPanelHeader: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.75rem",
    textTransform: "uppercase",
    fontWeight: "bold",
    color: "#64748B",
    marginBottom: "12px",
    letterSpacing: "0.5px"
  },
  exampleContentCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "20px",
    border: "2px solid #E2E8F0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)"
  },
  emojiBubble: {
    fontSize: "4.5rem",
    margin: "6px 0",
    lineHeight: 1
  },
  wordDescriptionBlock: {
    margin: "8px 0"
  },
  exampleWordTitle: {
    margin: "0 0 4px 0",
    fontSize: "1.6rem",
    fontWeight: "800",
    color: "#1E293B"
  },
  wordPhonicsSub: {
    margin: 0,
    fontSize: "0.85rem",
    color: "#475569"
  },
  inlinePhoneme: {
    backgroundColor: "#F3E8FF",
    color: "#7C3AED",
    padding: "2px 6px",
    borderRadius: "6px",
    fontWeight: "bold",
    marginLeft: "4px",
    marginRight: "4px"
  },
  successCheckRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "12px",
    backgroundColor: "#F0FDF4",
    padding: "6px 10px",
    borderRadius: "8px",
    border: "1px solid #DCFCE7"
  },
  checkIconBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  checkText: {
    fontSize: "0.7rem",
    color: "#166534",
    fontWeight: "bold"
  }
};